// api/fertilizerApi.js
// Axios-API Layer inkl. Bearer Token aus localStorage.getItem('token')

import axios from "axios";

export function createFertAxios(baseURL = '/api') {
  const instance = axios.create({ baseURL, timeout: 20000 });
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  return instance;
}

export const fertAxios = createFertAxios('/api');


function unwrapOk(res) {
  const data = res?.data;
  if (!data || data.ok !== true) {
    const msg = data?.error || "Unbekannter API Fehler";
    const err = new Error(msg);
    err.payload = data;
    throw err;
  }
  return data;
}

export const fertPhaseMap = {
  Keimung: "germination",
  Veg: "veg",
  Blüte: "flower",
  Flush: "flush",
};

export const fertPhaseUnmap = {
  germination: "Keimung",
  veg: "Veg",
  flower: "Blüte",
  flush: "Flush",
};

export function apiTaskToUiEvent(apiTask) {
  if (!apiTask) return null;
  return {
    id: apiTask.id,
    grow_setup_id: apiTask.grow_setup_id,
    plan_id: apiTask.plan_id ?? null,
    date_iso: apiTask.date_iso,
    type: apiTask.type,
    phase: fertPhaseUnmap[apiTask.phase] ?? apiTask.phase,
    week_index: apiTask.week_index,
    water_l: apiTask.water_l ?? null,
    targets: {
      ec: apiTask.targets?.ec ?? null,
      ph: apiTask.targets?.ph ?? null,
    },
    actuals: {
      ec: apiTask.actuals?.ec ?? null,
      ph: apiTask.actuals?.ph ?? null,
    },
    note: apiTask.note ?? "",
    done: Boolean(apiTask.done),
    recipe_lines: (apiTask.recipe_lines || []).map((l) => ({
      id: l.id,
      product_name: l.product_name,
      product_sku: l.product_sku ?? null,
      ml_per_l: l.ml_per_l ?? 0,
      sort_order: l.sort_order ?? 0,
    })),
  };
}

export function uiEventToApiTask(uiEvent) {
  if (!uiEvent) return null;

  const phase = fertPhaseMap[uiEvent.phase] || uiEvent.phase;

  return {
    grow_setup_id: uiEvent.grow_setup_id,
    plan_id: uiEvent.plan_id ?? null,
    date_iso: uiEvent.date_iso,
    type: uiEvent.type,
    phase,
    week_index: uiEvent.week_index,
    water_l: uiEvent.water_l ?? null,
    targets: {
      ec: uiEvent.targets?.ec ?? null,
      ph: uiEvent.targets?.ph ?? null,
    },
    actuals: {
      ec: uiEvent.actuals?.ec ?? null,
      ph: uiEvent.actuals?.ph ?? null,
    },
    note: uiEvent.note ?? "",
    done: Boolean(uiEvent.done),
    recipe_lines: (uiEvent.recipe_lines || []).map((l, idx) => ({
      id: l.id,
      product_name: l.product_name ?? "",
      product_sku: l.product_sku ?? null,
      ml_per_l: l.ml_per_l ?? 0,
      sort_order: l.sort_order ?? idx,
    })),
  };
}

export function localDateTimeToIsoZ(dateIsoLocal) {
  if (!dateIsoLocal) return null;
  return new Date(dateIsoLocal).toISOString();
}

export function createFertApi(http) {
  return {
    async getTasks({
      grow_setup_id,
      week_index = null,
      phase = null,
      open_only = false,
      types = null,
      after = null,
      before = null,
    }) {
      const params = {
        grow_setup_id,
        ...(week_index != null ? { week_index } : {}),
        ...(phase ? { phase: fertPhaseMap[phase] || phase } : {}),
        ...(open_only ? { open_only: 1 } : {}),
        ...(after ? { after } : {}),
        ...(before ? { before } : {}),
      };
      if (types) params.types = Array.isArray(types) ? types.join(",") : types;
      const res = await http.get("/fert/tasks", { params });
      const data = unwrapOk(res);
      return { tasks: (data.tasks || []).map(apiTaskToUiEvent), raw: data };
    },

    async getTask(task_id) {
      const res = await http.get(`/fert/tasks/${task_id}`);
      const data = unwrapOk(res);
      return { task: apiTaskToUiEvent(data.task), raw: data };
    },

    async createTask(uiEventOrDraft) {
      const payload = uiEventToApiTask(uiEventOrDraft);
      if (!payload.date_iso && uiEventOrDraft?.date_iso_local) {
        payload.date_iso = localDateTimeToIsoZ(uiEventOrDraft.date_iso_local);
      }
      const res = await http.post("/fert/tasks", payload);
      const data = unwrapOk(res);
      return { task: apiTaskToUiEvent(data.task), raw: data };
    },

    async updateTask(task_id, uiEventOrPatch, { method = "PUT" } = {}) {
      const looksLikeFullEvent =
        uiEventOrPatch?.date_iso || uiEventOrPatch?.type || uiEventOrPatch?.phase || uiEventOrPatch?.week_index != null;
      const payload = looksLikeFullEvent ? uiEventToApiTask(uiEventOrPatch) : { ...uiEventOrPatch };
      if (!payload.date_iso && uiEventOrPatch?.date_iso_local) {
        payload.date_iso = localDateTimeToIsoZ(uiEventOrPatch.date_iso_local);
      }
      if (payload.phase) payload.phase = fertPhaseMap[payload.phase] || payload.phase;
      const res =
        method === "PATCH"
          ? await http.patch(`/fert/tasks/${task_id}`, payload)
          : await http.put(`/fert/tasks/${task_id}`, payload);
      const data = unwrapOk(res);
      return { task: apiTaskToUiEvent(data.task), raw: data };
    },

    async toggleTaskDone(task_id) {
      const res = await http.patch(`/fert/tasks/${task_id}/toggle_done`);
      const data = unwrapOk(res);
      return { task: apiTaskToUiEvent(data.task), raw: data };
    },

    async deleteTask(task_id) {
      const res = await http.delete(`/fert/tasks/${task_id}`);
      const data = unwrapOk(res);
      return { ok: true, raw: data };
    },

    async getPlans({ grow_setup_id }) {
      const res = await http.get("/fert/plans", { params: { grow_setup_id } });
      const data = unwrapOk(res);
      return { plans: data.plans || [], raw: data };
    },

    async createPlan({ grow_setup_id, name = null, start_date = null, timezone = "Europe/Berlin", is_active = true }) {
      const res = await http.post("/fert/plans", { grow_setup_id, name, start_date, timezone, is_active });
      const data = unwrapOk(res);
      return { plan: data.plan, raw: data };
    },

    async getTemplates({ user_id }) {
      const res = await http.get("/fert/templates", { params: { user_id } });
      const data = unwrapOk(res);
      return { templates: data.templates || [], raw: data };
    },

    async createTemplate(template) {
      const res = await http.post("/fert/templates", template);
      const data = unwrapOk(res);
      return { template: data.template, raw: data };
    },

    async updateTemplate(template_id, templatePatch, { method = "PUT" } = {}) {
      const res =
        method === "PATCH"
          ? await http.patch(`/fert/templates/${template_id}`, templatePatch)
          : await http.put(`/fert/templates/${template_id}`, templatePatch);
      const data = unwrapOk(res);
      return { template: data.template, raw: data };
    },

    async deleteTemplate(template_id) {
      const res = await http.delete(`/fert/templates/${template_id}`);
      const data = unwrapOk(res);
      return { ok: true, raw: data };
    },

    async applyTemplate(
      template_id,
      {
        grow_setup_id,
        plan_id = null,
        create_plan_if_missing = true,
        plan_name = null,
        start_date = null,
        timezone = "Europe/Berlin",
        clear_existing = false,
        week_from = null,
        week_to = null,
        phase = null,
      }
    ) {
      const payload = {
        grow_setup_id,
        ...(plan_id != null ? { plan_id } : {}),
        create_plan_if_missing,
        ...(plan_name ? { plan_name } : {}),
        ...(start_date ? { start_date } : {}),
        timezone,
        clear_existing,
        ...(week_from != null ? { week_from } : {}),
        ...(week_to != null ? { week_to } : {}),
        ...(phase ? { phase: fertPhaseMap[phase] || phase } : {}),
      };
      const res = await http.post(`/fert/templates/${template_id}/apply`, payload);
      const data = unwrapOk(res);
      return { result: data, raw: data };
    },
  };
}

/** Standard-API (bestehende Routen /api/fert/...) – Backward-Kompatibilität */
export const fertApi = createFertApi(fertAxios);

/** v4-API – nutzt die neuen Routen /api/v4/fert/... */
export const fertApiV4 = createFertApi(createFertAxios('/api/v4'));
